import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import BackButton from "../../../components/BackButton";
import TimeAndSeat from "./TimeAndSeat";
import CheckoutRequired from "./CheckoutRequired";
import "../../../styles/GetTicket.scss";
import { FaCalendar } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { IoPerson } from "react-icons/io5";
import {
  tryFetch,
  IResponse,
} from "../../../helpers/FetchAPI";
import { getTokenOrDefault } from "../../../helpers/Storage";
import { useEventRating } from "../../hooks/useEventRating";

const CalendarIcon =
  FaCalendar as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

const LocationIcon =
  FaLocationDot as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

const PersonIcon =
  IoPerson as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

type Customer = {
  name: string;
  email: string;
  phone: string;
  birthdate: string;
};

interface Ticket {
  id: string;
  ticket_type: string;
  price: string;
  available_slot: string;
  ticket_count?: number;
  capacity?: number;
  seats: unknown[];
}

interface EventData {
  id: string;
  event_title: string;
  venue: string;
  event_rating: string;
  description: string;
  organizer: string;
  dates: string[];
  is_seat_section: boolean;
  is_single_ticket: boolean;
  tickets: Ticket[];
  house_rules: unknown[];
}

const toBoolean = (
  value: unknown
): boolean => {
  if (
    typeof value ===
    "boolean"
  ) {
    return value;
  }

  if (
    typeof value ===
    "number"
  ) {
    return value === 1;
  }

  if (
    typeof value ===
    "string"
  ) {
    const normalizedValue =
      value
        .trim()
        .toLowerCase();

    return (
      normalizedValue ===
        "true" ||
      normalizedValue === "1"
    );
  }

  return false;
};

const getFirstAvailableDate = (
  dates: string[]
): string | null => {
  if (!Array.isArray(dates)) {
    return null;
  }

  const sortedDates = [
    ...dates,
  ]
    .filter(
      (date) =>
        !Number.isNaN(
          new Date(
            date
          ).getTime()
        )
    )
    .sort(
      (
        firstDate,
        secondDate
      ) =>
        new Date(
          firstDate
        ).getTime() -
        new Date(
          secondDate
        ).getTime()
    );

  return sortedDates[0] || null;
};

const formatEventDateRange = (
  dates: string[]
): string => {
  if (
    !Array.isArray(dates) ||
    dates.length === 0
  ) {
    return "No Dates";
  }

  const sortedDates = [
    ...dates,
  ]
    .filter(
      (date) =>
        !Number.isNaN(
          new Date(
            date
          ).getTime()
        )
    )
    .sort(
      (
        firstDate,
        secondDate
      ) =>
        new Date(
          firstDate
        ).getTime() -
        new Date(
          secondDate
        ).getTime()
    );

  if (
    sortedDates.length === 0
  ) {
    return "No Dates";
  }

  const formatDate = (
    date: string
  ) =>
    new Date(
      date
    ).toLocaleDateString(
      "en-US",
      {
        month: "long",
        day: "numeric",
        year: "numeric",
      }
    );

  const startDate =
    formatDate(
      sortedDates[0]
    );

  const endDate =
    formatDate(
      sortedDates[
        sortedDates.length - 1
      ]
    );

  return startDate === endDate
    ? startDate
    : `${startDate} - ${endDate}`;
};

const GetTicketRequired:
  React.FC = () => {
  const { slug } = useParams();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const eventId =
    location.state?.eventId;

  const [
    eventData,
    setEventData,
  ] = useState<EventData | null>(
    null
  );

  const [
    selectedDate,
    setSelectedDate,
  ] = useState<string | null>(
    null
  );

  const [
    selectedTime,
    setSelectedTime,
  ] = useState<string | null>(
    null
  );

  const [
    selectedTicketType,
    setSelectedTicketType,
  ] = useState<string | null>(
    null
  );

  const [
    selectedSeatIds,
    setSelectedSeatIds,
  ] = useState<string[]>([]);

  const [
    customers,
    setCustomers,
  ] = useState<Customer[]>([
    {
      name: "",
      email: "",
      phone: "",
      birthdate: "",
    },
  ]);

  const formattedRating =
    useEventRating(
      eventData?.event_rating ||
        ""
    );

  useEffect(() => {
    let isMounted = true;

    const fetchEvent =
      async () => {
        try {
          if (!eventId) {
            console.error(
              "Event ID not found from navigation state."
            );

            if (isMounted) {
              setEventData(
                null
              );
            }

            return;
          }

          const res: IResponse =
            await tryFetch({
              url: `/events/${eventId}?timestamp=${Date.now()}`,
              method: "GET",
            });

          if (!isMounted) {
            return;
          }

          if (
            res.is_success &&
            res.data
          ) {
            const foundEvent =
              res.data as any;

            const mappedEvent:
              EventData = {
              id: foundEvent.id,

              event_title:
                foundEvent.event_title,

              venue:
                foundEvent.venue,

              event_rating:
                foundEvent.event_rating,

              description:
                foundEvent.description,

              organizer:
                foundEvent.organizer,

              dates:
                Array.isArray(
                  foundEvent.all_dates
                )
                  ? foundEvent.all_dates
                  : [],

              is_seat_section:
                toBoolean(
                  foundEvent.is_seat_section
                ),

              is_single_ticket:
                toBoolean(
                  foundEvent.is_single_ticket
                ),

              tickets:
                Array.isArray(
                  foundEvent.tickets
                )
                  ? foundEvent.tickets.map(
                      (
                        ticket: any
                      ) => {
                        const capacity =
                          Number(
                            ticket.capacity ??
                              0
                          );

                        const ticketCount =
                          Number(
                            ticket.ticket_count ??
                              0
                          );

                        const availableSlot =
                          Math.max(
                            capacity -
                              ticketCount,
                            0
                          );

                        return {
                          id: ticket.id,

                          ticket_type:
                            ticket.name,

                          price:
                            ticket.ticket_price,

                          available_slot:
                            String(
                              availableSlot
                            ),

                          capacity,

                          ticket_count:
                            ticketCount,

                          seats: [],
                        };
                      }
                    )
                  : [],

              house_rules:
                foundEvent.house_rules ||
                [],
            };

            setSelectedDate(
              null
            );

            setSelectedTime(
              null
            );

            setSelectedTicketType(
              null
            );

            setSelectedSeatIds(
              []
            );

            setEventData(
              mappedEvent
            );
          } else {
            console.error(
              "Failed to fetch event:",
              res.error
            );

            setEventData(
              null
            );
          }
        } catch (error) {
          console.error(
            "Error fetching event:",
            error
          );

          if (isMounted) {
            setEventData(
              null
            );
          }
        }
      };

    fetchEvent();

    return () => {
      isMounted = false;
    };
  }, [eventId, slug]);

  const handleAddTicket =
    () => {
      setCustomers(
        (
          previousCustomers
        ) => [
          ...previousCustomers,
          {
            name: "",
            email: "",
            phone: "",
            birthdate: "",
          },
        ]
      );
    };

  const handleRemoveTicket = (
    index: number
  ) => {
    setCustomers(
      (
        previousCustomers
      ) =>
        previousCustomers.filter(
          (
            _,
            customerIndex
          ) =>
            customerIndex !==
            index
        )
    );
  };

  const handleChange = (
    index: number,
    field: keyof Customer,
    value: string
  ) => {
    setCustomers(
      (
        previousCustomers
      ) => {
        return previousCustomers.map(
          (
            customer,
            customerIndex
          ) => {
            if (
              customerIndex !==
              index
            ) {
              return customer;
            }

            return {
              ...customer,
              [field]: value,
            };
          }
        );
      }
    );
  };

  const allCustomersValid =
    customers.every(
      (customer) => {
        return Boolean(
          customer.name.trim() &&
            customer.email.trim() &&
            customer.phone.trim() &&
            customer.birthdate
        );
      }
    );

  const ticketSelected =
    useMemo(() => {
      if (!eventData) {
        return undefined;
      }

      if (
        eventData.is_seat_section &&
        selectedSeatIds.length >
          0
      ) {
        return eventData.tickets.find(
          (ticket) =>
            ticket.id ===
            selectedSeatIds[0]
        );
      }

      if (
        !selectedTicketType
      ) {
        return undefined;
      }

      return eventData.tickets.find(
        (ticket) =>
          ticket.ticket_type
            .trim()
            .toLowerCase() ===
          selectedTicketType
            .trim()
            .toLowerCase()
      );
    }, [
      eventData,
      selectedTicketType,
      selectedSeatIds,
    ]);

  const hasCompletedSeatSelection =
    !eventData?.is_seat_section ||
    selectedSeatIds.length ===
      customers.length;

  const handleProceed = async (
    ticketId:
      | string
      | undefined
  ) => {
    if (!eventData) {
      alert(
        "Event data is unavailable."
      );

      return;
    }

    if (!ticketId) {
      alert(
        eventData.is_seat_section
          ? "Please select your seat."
          : "Please select a ticket type."
      );

      return;
    }

    const firstAvailableDate =
      getFirstAvailableDate(
        eventData.dates
      );

    const payloadSelectedDate =
      eventData.is_single_ticket
        ? firstAvailableDate
        : selectedDate;

    if (
      !payloadSelectedDate
    ) {
      alert(
        "Please select a date."
      );

      return;
    }

    if (!selectedTime) {
      alert(
        "Please select a time."
      );

      return;
    }

    if (
      eventData.is_seat_section &&
      selectedSeatIds.length !==
        customers.length
    ) {
      alert(
        `Please select exactly ${
          customers.length
        } ${
          customers.length === 1
            ? "seat"
            : "seats"
        } based on the number of tickets you are buying.`
      );

      return;
    }

    if (
      !allCustomersValid
    ) {
      alert(
        "Please complete all customer details."
      );

      return;
    }

    const token =
      getTokenOrDefault();

    const payload = {
      ticket_type_id:
        ticketId,

      ticket_count:
        customers.length.toString(),

      selected_dates: [
        payloadSelectedDate,
      ],

      /*
       * Included only for
       * seat-section events.
       */
      seat_ids:
        eventData.is_seat_section
          ? selectedSeatIds
          : undefined,

      customers,
    };

    console.log(
      "Required customer data payload:",
      payload
    );

    try {
      const res: IResponse =
        await tryFetch({
          url: `/sec/events/${eventData.id}/tickets/${ticketId}`,
          method: "POST",
          token,
          payload,
        });

      if (res.is_success) {
        const createdId =
          (res.data as any)
            ?.id || ticketId;

        navigate(
          `/preview/${createdId}`
        );
      } else {
        alert(
          res.error ||
            "Checkout failed."
        );
      }
    } catch (error) {
      console.error(error);

      alert(
        "Something went wrong."
      );
    }
  };

  if (!eventData) {
    return (
      <div>Loading...</div>
    );
  }

  const isSingleTicket =
    eventData.is_single_ticket;

  const isSeatSection =
    eventData.is_seat_section;

  return (
    <div className="get-ticket">
      <BackButton />

      <h1 className="page-title">
        Get Tickets
      </h1>

      <hr className="divider" />

      <div className="event-details">
        <h2>
          {
            eventData.event_title
          }
        </h2>

        <p className="e-organizer">
          by {eventData.organizer}
        </p>

        <p>
          <CalendarIcon className="icon" />

          {formatEventDateRange(
            eventData.dates
          )}
        </p>

        <p>
          <LocationIcon className="icon" />

          {eventData.venue}
        </p>

        <p>
          <PersonIcon className="icon" />

          {formattedRating}
        </p>
      </div>

      <TimeAndSeat
        key={`${eventData.id}-${String(
          isSingleTicket
        )}-${String(
          isSeatSection
        )}`}
        tickets={
          eventData.tickets
        }
        availableDates={
          eventData.dates
        }
        isSeatSection={
          isSeatSection
        }
        isSingleTicket={
          isSingleTicket
        }
        maxSelectableSeats={
          isSeatSection
            ? customers.length
            : undefined
        }
        showSeatLimitNote={
          isSeatSection
        }
        onDateSelect={
          setSelectedDate
        }
        onTimeSelect={
          setSelectedTime
        }
        onTicketSelect={
          setSelectedTicketType
        }
        onSeatSelect={
          setSelectedSeatIds
        }
      />

      <div className="customer-forms">
        {customers.map(
          (
            customer,
            index
          ) => (
            <div
              key={index}
              className="customer-form-container"
            >
              <div className="ticket-label">
                x{index + 1}{" "}
                Ticket
              </div>

              <input
                type="text"
                placeholder="Name"
                value={
                  customer.name
                }
                onChange={(
                  event
                ) =>
                  handleChange(
                    index,
                    "name",
                    event.target
                      .value
                  )
                }
              />

              <input
                type="email"
                placeholder="Email Address"
                value={
                  customer.email
                }
                onChange={(
                  event
                ) =>
                  handleChange(
                    index,
                    "email",
                    event.target
                      .value
                  )
                }
              />

              <input
                type="text"
                placeholder="Phone Number"
                value={
                  customer.phone
                }
                onChange={(
                  event
                ) =>
                  handleChange(
                    index,
                    "phone",
                    event.target
                      .value
                  )
                }
              />

              <input
                type="date"
                value={
                  customer.birthdate
                }
                onChange={(
                  event
                ) =>
                  handleChange(
                    index,
                    "birthdate",
                    event.target
                      .value
                  )
                }
              />

              {customers.length >
                1 && (
                <button
                  type="button"
                  className="remove-ticket"
                  onClick={() =>
                    handleRemoveTicket(
                      index
                    )
                  }
                >
                  Remove
                </button>
              )}
            </div>
          )
        )}

        <div className="add-ticket-section">
          <button
            type="button"
            className="add-ticket"
            onClick={
              handleAddTicket
            }
          >
            + Get more Ticket
          </button>
        </div>
      </div>

      <CheckoutRequired
        ticket={
          ticketSelected
        }
        ticketCount={
          customers.length
        }
        selectedDate={
          selectedDate
        }
        selectedTime={
          selectedTime
        }
        allCustomersValid={
          allCustomersValid &&
          hasCompletedSeatSelection
        }
        onProceed={
          handleProceed
        }
      />
    </div>
  );
};

export default GetTicketRequired;